<!--
使用示例：

单张图片：
<ImagePreviewDialog
  v-model="showPreview"
  :image-url="singleImageUrl"
/>

多张图片：
<ImagePreviewDialog
  v-model="showPreview"
  :image-list="imageList"
  :initial-index="selectedIndex"
  :current-material-index="materialIndex"
  :is-delete="true"
  @delete="handleDeleteImage"
/>

Props:
- modelValue: Boolean - 控制对话框显示/隐藏
- imageUrl: String - 单张图片的URL（可选，向后兼容）
- imageList: Array<String> - 图片URL数组
- initialIndex: Number - 初始显示的图片索引（默认0）
- currentMaterialIndex: Number - 当前材料索引（默认0）
- isDelete: Boolean - 是否显示删除按钮（默认false）
- type: String - 类型（可选，向后兼容）

Emits:
- update:modelValue - 更新对话框显示状态
- close - 关闭对话框事件
- delete - 删除图片事件，参数为(材料索引, 图片索引)

删除功能：
- 点击删除按钮会向父组件发送delete事件，传递材料索引和图片索引
- 父组件需要监听@delete事件并处理图片删除逻辑
- 删除最后一张图片时会自动关闭对话框
- 删除其他图片时会自动切换到下一张或上一张图片
-->

<template>
  <v-dialog
    v-model="dialogModel"
    fullscreen
    :scrim="false"
    transition="dialog-bottom-transition"
  >
    <v-card class="image-preview-dialog d-flex flex-column">
      <!-- 关闭按钮 -->
      <v-btn
        icon="mdi-close"
        variant="text"
        size="large"
        class="close-btn"
        @click="handleClose"
      />

      <!-- 图片容器 -->
      <div class="image-container d-flex align-center justify-center" @wheel.prevent="handleWheel" @click="handleClose">
        <img
          :src="currentImageUrl"
          class="preview-image"
          :style="{
            transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${scale}) rotate(${rotation}deg)`,
          }"
          draggable="false"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @click.stop
        >
      </div>

      <!-- 左右切换按钮 -->
      <v-btn
        v-if="imageList.length > 1"
        icon="mdi-chevron-left"
        variant="text"
        size="large"
        class="nav-btn prev-btn"
        @click="prevImage"
        :disabled="currentIndex === 0"
      />
      <v-btn
        v-if="imageList.length > 1"
        icon="mdi-chevron-right"
        variant="text"
        size="large"
        class="nav-btn next-btn"
        @click="nextImage"
        :disabled="currentIndex === imageList.length - 1"
      />

      <!-- 图片指示器 -->
      <div v-if="imageList.length > 1" class="image-indicator">
        <span class="indicator-text">{{ currentIndex + 1 }} / {{ imageList.length }}</span>
      </div>

      <!-- 底部工具栏 -->
      <div class="bottom-toolbar">
        <v-btn
          icon="mdi-minus"
          variant="text"
          size="large"
          @click="zoomOut"
          :disabled="scale <= 0.5"
        />
        <v-btn
          icon="mdi-plus"
          variant="text"
          size="large"
          @click="zoomIn"
          :disabled="scale >= 3"
        />
        <v-btn
          icon="mdi-rotate-left"
          variant="text"
          size="large"
          @click="rotateLeft"
        />
        <v-btn
          icon="mdi-rotate-right"
          variant="text"
          size="large"
          @click="rotateRight"
        />
        <v-divider vertical class="mx-2" />
        <v-btn
          icon="mdi-download"
          variant="text"
          size="large"
          @click="handleDownload"
          :disabled="!currentImageUrl"
        />
        <v-divider vertical class="mx-2" v-if="isDelete" />
        <v-btn
          v-if="isDelete"
          icon="mdi-delete"
          variant="text"
          size="large"
          color="error"
          @click="handleDelete"
          :disabled="imageList.length === 0"
        />
      </div>

      <!-- 删除确认对话框 -->
      <v-dialog
        v-model="showDeleteConfirm"
        max-width="400"
        persistent
      >
        <v-card class="delete-confirm-dialog">
          <!-- <v-card-title class="d-flex align-center">
            <v-icon icon="mdi-delete" color="error" class="mr-2"></v-icon>
            <span>确认删除</span>
          </v-card-title> -->
          <v-card-text>
            <p>确定要删除这张图片吗？</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              variant="tonal"
              @click="showDeleteConfirm = false"
            >
              取消
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="confirmDelete"
            >
              确定
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onUnmounted } from 'vue'

export default defineComponent({
  name: 'ImagePreviewDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    imageUrl: {
      type: String,
      required: false,
      default: ''
    },
    imageList: {
      type: Array as () => string[],
      default: () => []
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    currentMaterialIndex: {
      type: Number,
      default: 0
    },
    isDelete: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['update:modelValue', 'close', 'delete'],
  setup(props, { emit }) {
    // 对话框双向绑定
    const dialogModel = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    // 当前图片索引
    const currentIndex = ref(props.initialIndex)
    const currentMaterialIndex = ref(props.currentMaterialIndex)
    const isDelete = computed(() => props.isDelete)
    const type = computed(() => props.type || '')
    // 当前图片URL
    const currentImageUrl = computed(() => {
      if (props.imageList && props.imageList.length > 0) {
        return props.imageList[currentIndex.value] || ''
      }
      return props.imageUrl || ''
    })

    // 监听imageList变化，重置索引
    watch(() => props.imageList, (newList) => {
      if (newList && newList.length > 0) {
        currentIndex.value = Math.min(props.initialIndex, newList.length - 1)
      }
    })

    // 监听currentMaterialIndex变化
    watch(() => props.currentMaterialIndex, (newIndex) => {
      currentMaterialIndex.value = newIndex
    })

    // 监听type变化
    watch(() => props.type, (newType) => {
      console.log('type changed:', newType)
    })

    // 缩放和旋转状态
    const scale = ref(1)
    const rotation = ref(0)

    // 拖动状态
    const isDragging = ref(false)
    const dragStart = ref({ x: 0, y: 0 })
    const dragOffset = ref({ x: 0, y: 0 })

    // 处理关闭
    const handleClose = () => {
      emit('update:modelValue', false)
      // 重置状态
      scale.value = 1
      rotation.value = 0
      dragOffset.value = { x: 0, y: 0 }
      currentIndex.value = props.initialIndex
    }

    // 图片切换功能
    const nextImage = () => {
      if (currentIndex.value < props.imageList.length - 1) {
        currentIndex.value++
        resetImageState()
      }
    }

    const prevImage = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--
        resetImageState()
      }
    }

    // 重置图片状态（缩放、旋转、拖动）
    const resetImageState = () => {
      scale.value = 1
      rotation.value = 0
      dragOffset.value = { x: 0, y: 0 }
    }

    // 删除功能
    const handleDelete = () => {
      if (props.imageList.length > 0) {
        // 显示确认对话框
        showDeleteConfirm.value = true
      }
    }

    // 缩放功能
    const zoomIn = () => {
      if (scale.value < 3) {
        scale.value = Math.min(3, scale.value + 0.2)
      }
    }

    const zoomOut = () => {
      if (scale.value > 0.5) {
        scale.value = Math.max(0.5, scale.value - 0.2)
      }
    }

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    }

    // 旋转功能
    const rotateLeft = () => {
      rotation.value = (rotation.value - 90) % 360
    }

    const rotateRight = () => {
      rotation.value = (rotation.value + 90) % 360
    }

    // 下载功能
    const handleDownload = async () => {
      if (!currentImageUrl.value) return
      
      try {
        // 通过 fetch 获取图片数据
        const response = await fetch(currentImageUrl.value)
        const blob = await response.blob()
        
        // 创建 blob URL
        const blobUrl = window.URL.createObjectURL(blob)
        
        // 创建下载链接
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `image_${currentIndex.value + 1}.${getFileExtension(currentImageUrl.value)}`
        
        // 添加到 DOM 中，点击后移除
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理 blob URL
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('下载图片失败:', error)
        // 如果 fetch 失败，尝试直接下载
        try {
          const link = document.createElement('a')
          link.href = currentImageUrl.value
          link.download = `image_${currentIndex.value + 1}.${getFileExtension(currentImageUrl.value)}`
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } catch (fallbackError) {
          console.error('备用下载方法也失败:', fallbackError)
          // 最后尝试在新窗口中打开
          window.open(currentImageUrl.value, '_blank')
        }
      }
    }

    // 获取文件扩展名
    const getFileExtension = (url: string) => {
      const match = url.match(/\.([^.?]+)(\?|$)/)
      return match ? match[1] : 'jpg'
    }

    // 拖动功能
    const startDrag = (e: MouseEvent) => {
      isDragging.value = true
      dragStart.value = {
        x: e.clientX - dragOffset.value.x,
        y: e.clientY - dragOffset.value.y
      }
    }

    const onDrag = (e: MouseEvent) => {
      if (isDragging.value) {
        dragOffset.value = {
          x: e.clientX - dragStart.value.x,
          y: e.clientY - dragStart.value.y
        }
      }
    }

    const stopDrag = () => {
      isDragging.value = false
    }

    // 键盘导航
    const handleKeydown = (e: KeyboardEvent) => {
      if (!props.modelValue) return

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (props.imageList.length > 1) {
            prevImage()
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (props.imageList.length > 1) {
            nextImage()
          }
          break
        case 'Escape':
          e.preventDefault()
          handleClose()
          break
      }
    }

    // 监听键盘事件
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        document.addEventListener('keydown', handleKeydown)
      } else {
        document.removeEventListener('keydown', handleKeydown)
      }
    })

    // 组件卸载时清理事件监听器
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown)
    })

    // 删除确认对话框
    const showDeleteConfirm = ref(false)
    const confirmDelete = () => {
      if (props.imageList.length > 0) {
        // 向父组件发送删除事件，传递当前图片索引
        emit('delete', currentMaterialIndex.value, currentIndex.value, type.value)
        props.imageList.splice(currentIndex.value, 1)

        console.log('props.imageList.length', props.imageList, props.imageList.length)
        // 如果删除后没有图片了，关闭对话框
        if (props.imageList.length === 0) {
          handleClose()
        } else if (props.imageList.length === 1) {
          currentIndex.value = 0
        } else {
          // 如果删除的不是最后一张图片，显示下一张
          // 如果删除的是最后一张图片，显示上一张
          if (currentIndex.value === props.imageList.length && props.imageList.length > 1) {
            currentIndex.value = props.imageList.length - 1
            console.log('currentIndex.value', currentIndex.value, props.imageList.length)
          }
          resetImageState()
        }
      }
      showDeleteConfirm.value = false
    }

    return {
      dialogModel,
      currentIndex,
      currentMaterialIndex,
      isDelete,
      type,
      currentImageUrl,
      scale,
      rotation,
      dragOffset,
      handleClose,
      nextImage,
      prevImage,
      resetImageState,
      zoomIn,
      zoomOut,
      handleWheel,
      rotateLeft,
      rotateRight,
      handleDownload,
      startDrag,
      onDrag,
      stopDrag,
      handleDelete,
      showDeleteConfirm,
      confirmDelete
    }
  }
})
</script>

<style scoped>
.image-preview-dialog {
  background-color: rgba(0, 0, 0, 0.9);
  height: 100vh;
  width: 100vw;
}

.close-btn {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 100;
  color: white;
}

.image-container {
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.preview-image {
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: grab;
  transition: transform 0.2s ease;
  transform-origin: center;
}

.preview-image:active {
  cursor: grabbing;
}

.image-preview-dialog :deep(.nav-btn) {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  color: white;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  width: 88px;
  height: 88px;
}

.image-preview-dialog .nav-btn :deep(.mdi){
  font-size: 90px!important;
}

.image-preview-dialog .prev-btn {
  left: 100px;
}

.image-preview-dialog .next-btn {
  right: 100px;
}
.nav-btn:hover {
  background: rgba(0, 0, 0, 0.5);
}

.nav-btn:disabled {
  color: rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.1);
}

.prev-btn {
  left: 16px;
}

.next-btn {
  right: 16px;
}

.image-indicator {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 20px;
  border-radius: 40px;
}

.indicator-text {
  color: #333;
  font-size: 24px;
  font-weight: 500;
}

.bottom-toolbar {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 8px;
  z-index: 100;
}

.bottom-toolbar .v-btn {
  color: white;
}

.bottom-toolbar .v-btn:disabled {
  color: rgba(255, 255, 255, 0.3);
}

/* 删除确认对话框样式 */
.delete-confirm-dialog {
  background-color: #2B2D33 !important;
}

.delete-confirm-dialog :deep(.v-card-title) {
  color: #fff;
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-card-text) {
  color: rgba(255, 255, 255, 0.8);
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-card-actions) {
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-btn) {
  color: #fff;
}

.delete-confirm-dialog :deep(.v-btn--variant-text) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.delete-confirm-dialog :deep(.v-btn--variant-text:hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
